import * as React from "react";

import type { LucideIcon } from "lucide-react";
import { type VariantProps, cva } from "class-variance-authority";
import { cn } from "lib/utils";

const statusBadgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap",
  {
    variants: {
      status: {
        success:
          "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
        warning:
          "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300",
        info: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300",
        error:
          "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300",
        neutral:
          "border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-300",
        pending:
          "border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-800 dark:bg-cyan-950 dark:text-cyan-300",
      },
    },
    defaultVariants: {
      status: "neutral",
    },
  },
);

const dotColorMap: Record<string, string> = {
  success: "bg-emerald-500",
  warning: "bg-amber-500",
  info: "bg-blue-500",
  error: "bg-red-500",
  neutral: "bg-gray-500",
  pending: "bg-cyan-500",
};

function StatusBadge({
  status,
  label,
  icon: Icon,
  showDot = true,
  className,
  ...props
}: {
  label: string;
  icon?: LucideIcon;
  showDot?: boolean;
} & VariantProps<typeof statusBadgeVariants> &
  Omit<React.ComponentProps<"span">, "children">) {
  const resolvedStatus = status ?? "neutral";

  return (
    <span
      data-slot="status-badge"
      role="status"
      aria-label={`Estado: ${label}`}
      className={cn(statusBadgeVariants({ status }), className)}
      {...props}
    >
      {showDot && (
        <span
          className={cn(
            "size-1.5 shrink-0 rounded-full",
            dotColorMap[resolvedStatus],
          )}
        />
      )}
      {Icon && <Icon className="size-3 shrink-0" />}
      {label}
    </span>
  );
}

export { StatusBadge, statusBadgeVariants };
